import React, { useState, useEffect } from "react";
import styled, { keyframes, css } from "styled-components";
import { TRANSACTION_OPTIONS } from "../../utils/Enum";

// --- SYSTEM THEMING DESIGN TOKENS ---
const THEME = {
  mainBg: "#080c16",
  secondaryBg: "#131a30",
  mainColor: "#FF6978",
  secondaryColor: "#b266ff",
  accentColor: "#868be3",
  background: "#0d1324",
  successColor: "#05c483",
  warningColor: "#ffb74d",
  errorColor: "#ff4d4d",
};

// --- ANIMATION KEYFRAMES ---
const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
`;

const slideUp = keyframes`
  from { transform: translateY(12px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

// --- STYLED COMPONENTS ---
const ModalWrapper = styled.div`
  min-height: 100vh;
  color: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2%;
  user-select: none;
  position: relative;
  overflow: hidden;
  box-sizing: border-box;
  background-color: ${THEME.mainBg};
`;

const GlowBlob = styled.div`
  position: absolute;
  width: ${props => props.size || "288px"};
  height: ${props => props.size || "288px"};
  border-radius: 50%;
  pointer-events: none;
  filter: blur(80px);
  opacity: 0.15;
  top: ${props => props.top};
  bottom: ${props => props.bottom};
  left: ${props => props.left};
  right: ${props => props.right};
  background-color: ${props => props.color || THEME.mainColor};
`;

const ToastCard = styled.div`
  position: fixed;
  top: 24px;
  right: 24px;
  z-index: 100;
  padding: 16px;
  max-width: 384px;
  width: calc(100% - 48px);
  background-color: rgba(19, 26, 48, 0.95);
  border-radius: 12px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
  transition: all 0.3s ease;
  animation: ${slideUp} 0.3s ease-out forwards;
  border: 1px solid ${props => 
    props.type === "error" ? THEME.errorColor : 
    props.type === "warning" ? THEME.warningColor : THEME.successColor
  };
`;

const ToastHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const ToastTitle = styled.h5`
  font-weight: 800;
  font-size: 0.75rem;
  margin: 0;
  color: ${props => 
    props.type === "error" ? THEME.errorColor : 
    props.type === "warning" ? THEME.warningColor : THEME.successColor
  };
`;

const ToastMessage = styled.p`
  font-size: 11px;
  color: #94a3b8;
  margin-top: 4px;
  margin-bottom: 0;
  line-height: 1.5;
`;

const ToastClose = styled.button`
  color: #64748b;
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 0.75rem;
  padding: 0;
  margin-left: 12px;
  &:hover {
    color: #fff;
  }
`;

const ModalContainer = styled.div`
  width: 100%;
  padding: 2%;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.55);
  position: relative;
  z-index: 10;
  display: flex;
  flex-direction: column;
  gap: 24px;
  box-sizing: border-box;
  transition: all 0.3s ease;

  @media (min-width: 768px) {
    padding: 32px;
  }
`;

const ApprovedScreen = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px 0;
  animation: ${fadeIn} 0.4s ease-out forwards;
`;

const CheckCircle = styled.div`
  width: 64px;
  height: 64px;
  background-color: rgba(5, 196, 131, 0.1);
  border: 1px solid rgba(5, 196, 131, 0.3);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  font-size: 1.875rem;
  color: ${THEME.successColor};
`;

const ApprovedTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 900;
  color: #fff;
  margin: 0;
`;

const ApprovedDesc = styled.p`
  font-size: 0.75rem;
  color: #94a3b8;
  line-height: 1.625;
  max-width: 20rem;
  margin: 0 auto;
`;

const ReceiptBox = styled.div`
  background-color: ${THEME.secondaryBg};
  padding: 16px;
  border-radius: 12px;
  border: 1px solid #1e293b;
  display: flex;
  flex-direction: column;
  gap: 12px;
  text-align: left;
`;

const ReceiptRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.75rem;
`;

const ReceiptLabel = styled.span`
  color: #94a3b8;
`;

const ReceiptValue = styled.span`
  font-family: monospace;
  color: ${props => props.highlight ? THEME.mainColor : "#fff"};
  font-weight: 700;
`;

const PrimaryButton = styled.button`
  width: 100%;
  padding: 16px;
  font-size: 0.75rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-radius: 12px;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: ${props => props.bgColor || THEME.mainColor};
  color: ${props => props.color || "#080c16"};

  &:hover {
    filter: brightness(1.1);
  }

  &:active {
    transform: scale(0.98);
  }

  &:disabled {
    background-color: #1e293b;
    color: #64748b;
    cursor: wait;
    border: 1px solid #334155;
  }
`;

const OutlineButton = styled.button`
  width: 100%;
  padding: 12px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-radius: 12px;
  border: 1px solid #1e293b;
  background-color: ${THEME.secondaryBg};
  color: ${THEME.mainColor};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: rgba(255, 105, 120, 0.05);
    border-color: ${THEME.mainColor};
  }

  &:active {
    transform: scale(0.98);
  }
`;

const HeaderGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Badge = styled.span`
  font-size: 10px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  padding: 4px 12px;
  border-radius: 9999px;
  align-self: flex-start;
  background-color: rgba(255, 105, 120, 0.1);
  color: ${THEME.mainColor};
  border: 1px solid rgba(255, 105, 120, 0.2);
`;

const HeaderTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 900;
  color: #fff;
  margin: 0;
  letter-spacing: -0.025em;
`;

const HeaderDesc = styled.p`
  font-size: 0.75rem;
  color: #94a3b8;
  line-height: 1.5;
  margin: 0;
`;

const PlanCard = styled.div`
  background-color: ${THEME.secondaryBg};
  padding: 16px;
  border-radius: 12px;
  border: 1px solid #1e293b;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PlanDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const PlanLabel = styled.span`
  font-size: 9px;
  color: #64748b;
  text-transform: uppercase;
  font-weight: 700;
  letter-spacing: 0.05em;
`;

const PlanTitle = styled.h4`
  font-size: 0.75rem;
  font-weight: 800;
  color: #fff;
  margin: 0;
`;

const PlanPrice = styled.span`
  font-family: monospace;
  font-size: 1rem;
  font-weight: 900;
  color: ${THEME.mainColor};
`;

const CardsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionLabel = styled.span`
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
  display: block;
`;

const CardRowOption = styled.div`
  padding: 16px;
  border-radius: 12px;
  border: 1px solid ${props => props.active ? THEME.mainColor : "#1e293b"};
  background-color: ${props => props.active ? "rgba(255, 105, 120, 0.04)" : "rgba(15, 23, 42, 0.3)"};
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${props => props.active ? THEME.mainColor : "#334155"};
  }
`;

const CardDetailBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const CardBrandAvatar = styled.div`
  width: 48px;
  height: 32px;
  border-radius: 6px;
  background-color: #1e293b;
  border: 1px solid #334155;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const CardMetaBlock = styled.div``;

const CardHeadingText = styled.h5`
  font-size: 0.75rem;
  font-weight: 800;
  color: #fff;
  margin: 0;
`;

const CardNumberText = styled.p`
  font-size: 11px;
  color: #94a3b8;
  margin-top: 2px;
  margin-bottom: 0;
`;

const RightRadioGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ExpiryLabel = styled.span`
  font-size: 9px;
  font-weight: 900;
  text-transform: uppercase;
  color: #64748b;
`;

const RadioCircle = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 1px solid ${props => props.active ? THEME.mainColor : "#475569"};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const RadioInner = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${THEME.mainColor};
`;

const NewCardFormContainer = styled.div`
  border: 1px solid #1e293b;
  background-color: rgba(19, 26, 48, 0.4);
  padding: 16px;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  animation: ${slideUp} 0.25s cubic-bezier(0.16, 1, 0.3, 1) forwards;
`;

const FormHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 8px;
  border-bottom: 1px solid #1e293b;
`;

const FormTitleText = styled.span`
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #94a3b8;
`;

const FormCancelBtn = styled.button`
  font-size: 10px;
  font-weight: 700;
  color: ${THEME.errorColor};
  background: transparent;
  border: none;
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

const FormFieldsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const InputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const InputLabel = styled.label`
  font-size: 9px;
  text-transform: uppercase;
  font-weight: 800;
  color: #64748b;
  letter-spacing: 0.05em;
`;

const StyledInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  background-color: ${THEME.background};
  border: 1px solid #1e293b;
  border-radius: 10px;
  padding: 12px;
  font-size: 0.75rem;
  color: #fff;
  font-family: ${props => props.monospace ? "monospace" : "inherit"};

  &::placeholder {
    color: #475569;
  }

  &:focus {
    outline: none;
    border-color: ${THEME.mainColor};
  }
`;

const FormInlineRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

const CheckboxRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-top: 4px;
`;

const CheckboxInput = styled.input`
  width: 14px;
  height: 14px;
  accent-color: ${THEME.mainColor};
  background-color: ${THEME.background};
  border: 1px solid #1e293b;
  border-radius: 4px;
  cursor: pointer;
`;

const CheckboxLabelText = styled.label`
  font-size: 10px;
  font-weight: 700;
  color: #94a3b8;
  cursor: pointer;
`;

const ProgressCard = styled.div`
  padding: 16px;
  background-color: rgba(15, 23, 42, 0.4);
  border: 1px solid #1e293b;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ProgressHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 10px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
`;

const PulseStatus = styled.span`
  color: ${THEME.mainColor};
  animation: ${shimmer} 1.5s infinite ease-in-out;
`;

const ProgressBarTrack = styled.div`
  width: 100%;
  height: 6px;
  background-color: ${THEME.secondaryBg};
  border-radius: 9999px;
  overflow: hidden;
`;

const ProgressBarFill = styled.div`
  height: 100%;
  border-radius: 9999px;
  background: linear-gradient(to right, ${THEME.mainColor}, ${THEME.secondaryColor});
  width: 75%;
  animation: ${shimmer} 2s infinite linear;
  background-size: 200% 100%;
`;

const Disclaimer = styled.span`
  font-size: 9px;
  color: #475569;
  text-align: center;
  line-height: 1.5;
`;

const CardIcon = ({ brand }) => {
  if (brand?.toLowerCase() === "visa") {
    return (
      <svg className="w-10 h-6" style={{ color: "#60a5fa", width: "40px", height: "24px" }} viewBox="0 0 24 15" fill="currentColor">
        <path d="M0 0h24v15H0z" fill="none"/>
        <path d="M10.2 1.5h1.8l-1.1 8h-1.8z M5.5 1.5h1.9l1.2 8h-1.8L6.2 3.8h-.1L4.6 9.5H3L1.1 1.5h1.9l1 6.2h.1z M14.5 1.5h1.8L18 9.5h-1.8l-.3-1.6h-2.1l-.5 1.6h-1.8l2.2-8h.8z" />
      </svg>
    );
  }
  return (
    <svg className="w-10 h-6" style={{ color: "#f87171", width: "40px", height: "24px" }} viewBox="0 0 24 15" fill="currentColor">
      <path d="M0 0h24v15H0z" fill="none"/>
      <circle cx="9" cy="7.5" r="5" fill="#EB001B" opacity="0.8"/>
      <circle cx="15" cy="7.5" r="5" fill="#F79E1B" opacity="0.8"/>
    </svg>
  );
};

export default function CardPaymentOptions({type, body}) {
  const [selectedCardId, setSelectedCardId] = useState("primary"); // 'primary', 'backup', 'new'
  const [showNewCardForm, setShowNewCardForm] = useState(false);
  const [paymentState, setPaymentState] = useState("idle"); // 'idle', 'processing', 'completed'
  const [toast, setToast] = useState(null);

  // Cards state containing Primary and Backup records
  const [primaryCard, setPrimaryCard] = useState({
    id: "primary",
    brand: "Visa",
    last4: "4242",
    holder: "Alex Carter",
    expiry: "09/29"
  });

  const [backupCard, setBackupCard] = useState({
    id: "backup",
    brand: "Mastercard",
    last4: "8899",
    holder: "Alex Carter",
    expiry: "04/28"
  });

  // New card Form state inputs
  const [newCardName, setNewCardName] = useState("");
  const [newCardNumber, setNewCardNumber] = useState("");
  const [newCardExpiry, setNewCardExpiry] = useState("");
  const [newCardCVV, setNewCardCVV] = useState("");
  const [saveAsPrimary, setSaveAsPrimary] = useState(false);

  const triggerToast = (title, message, type = "success") => {
    setToast({ title, message, type });
    setTimeout(() => {
      setToast(null);
    }, 4500);
  };

  const handleCardNumberChange = (e) => {
    let value = e.target.value.replace(/\D/g, ""); // strip non-digits
    if (value.length > 16) value = value.slice(0, 16);
    const formatted = value.match(/.{1,4}/g)?.join(" ") || "";
    setNewCardNumber(formatted);
  };

  const handleExpiryChange = (e) => {
    let value = e.target.value.replace(/\D/g, ""); // strip non-digits
    if (value.length > 4) value = value.slice(0, 4);
    if (value.length >= 2) {
      value = value.slice(0, 2) + "/" + value.slice(2);
    }
    setNewCardExpiry(value);
  };

  const handleCVVChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 3);
    setNewCardCVV(value);
  };

  const handleOpenModifyForm = () => {
    setShowNewCardForm(true);
    setSelectedCardId("new");
    triggerToast("Form Activated", "Please enter your backup or replacement card details below.", "warning");
  };

  const handleCloseModifyForm = () => {
    setShowNewCardForm(false);
    setSelectedCardId("primary");
    setNewCardNumber("");
    setNewCardExpiry("");
    setNewCardCVV("");
    setNewCardName("");
  };

  const handleProcessPayment = (e) => {
    e.preventDefault();

    if (selectedCardId === "new") {
      if (!newCardName || !newCardNumber || !newCardExpiry || !newCardCVV) {
        triggerToast("Validation Error", "All fields are required for the new card.", "error");
        return;
      }
      if (newCardNumber.replace(/\s/g, "").length < 16) {
        triggerToast("Validation Error", "Please check your card number.", "error");
        return;
      }
      if (newCardExpiry.length < 5) {
        triggerToast("Validation Error", "Check card expiry format (MM/YY).", "error");
        return;
      }
      if (newCardCVV.length < 3) {
        triggerToast("Validation Error", "Invalid CVV number.", "error");
        return;
      }
    }

    setPaymentState("processing");
    triggerToast("Processing Gateway", "Authorizing transactional credentials with secure endpoints...", "warning");

    setTimeout(() => {
      setPaymentState("completed");
      triggerToast("Payment Captured!", "Your Swerl Unlimited Playback Pass is now active!", "success");

      if (selectedCardId === "new" && saveAsPrimary) {
        const last4Digits = newCardNumber.replace(/\s/g, "").slice(-4);
        const cardBrand = newCardNumber.startsWith("4") ? "Visa" : "Mastercard";
        setPrimaryCard({
          id: "primary",
          brand: cardBrand,
          last4: last4Digits,
          holder: newCardName,
          expiry: newCardExpiry
        });
      }
    }, 3000);
  };

  const resetSimulation = () => {
    setPaymentState("idle");
    handleCloseModifyForm();
  };

  return (
    <ModalWrapper>
      
      {/* Background aesthetics blobs without closing comment sequences inside */}
      <GlowBlob top="40px" left="25%" color="#f43f5e" size="288px" />
      <GlowBlob bottom="40px" right="25%" color="#a855f7" size="320px" />

      {/* Embedded Global Alert Toasts without closing comment sequences inside */}
      {toast && (
        <ToastCard type={toast.type}>
          <ToastHeader>
            <div>
              <ToastTitle type={toast.type}>{toast.title}</ToastTitle>
              <ToastMessage>{toast.message}</ToastMessage>
            </div>
            <ToastClose onClick={() => setToast(null)}>✕</ToastClose>
          </ToastHeader>
        </ToastCard>
      )}

      {/* Main card payment container modal without closing comment sequences inside */}
      <ModalContainer>
        
        {paymentState === "completed" ? (
          <ApprovedScreen>
            <CheckCircle>✓</CheckCircle>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <ApprovedTitle>Payment Approved</ApprovedTitle>
              <ApprovedDesc>
                Your card has been successfully authorized. Your Swerl balance is now updated and active.
              </ApprovedDesc>
            </div>

            <ReceiptBox>
              <ReceiptRow>
                <ReceiptLabel>Order Reference:</ReceiptLabel>
                <ReceiptValue>SWL-894-XRPB</ReceiptValue>
              </ReceiptRow>
              <ReceiptRow>
                <ReceiptLabel>Card Utilized:</ReceiptLabel>
                <ReceiptValue>
                  {selectedCardId === "primary" ? `${primaryCard.brand} •••• ${primaryCard.last4}` : 
                   selectedCardId === "backup" ? `${backupCard.brand} •••• ${backupCard.last4}` : "New Custom Card"}
                </ReceiptValue>
              </ReceiptRow>
              <ReceiptRow>
                <ReceiptLabel>Amount Charged:</ReceiptLabel>
                <ReceiptValue highlight>${body?.price} USD</ReceiptValue>
              </ReceiptRow>
            </ReceiptBox>

            <PrimaryButton bgColor={THEME.successColor} onClick={resetSimulation}>
              Back to Simulator Dashboard
            </PrimaryButton>
          </ApprovedScreen>
        ) : (
          <>
            
            {/* Header Description without closing comment sequences inside */}
            <HeaderGroup>
              <HeaderTitle>Card Payment Options</HeaderTitle>
              <HeaderDesc>
                Configure your payment credentials to top up streaming pass credits.
              </HeaderDesc>
            </HeaderGroup>

            {/* Total Balance / Checkout overview card without closing comment sequences inside */}
            <PlanCard>
              <PlanDetails>
                <PlanLabel>Plan Selected</PlanLabel>
                <PlanTitle>Swerl {(type === TRANSACTION_OPTIONS.PAY_STREAMING_SUBSCRIPTION || type === TRANSACTION_OPTIONS.PAY_AS_YOU_GO_STREAMING_SUBSCRIPTION) ? 'Streaming Subscription' : type === TRANSACTION_OPTIONS.TOP_UP_STREAMING_SUBSCRIPTION ? 'Streaming Subscription Top-up': 'Tipping Balance Top=up'}</PlanTitle>
              </PlanDetails>
              <div style={{ textAlign: "right" }}>
                <PlanLabel>Price</PlanLabel>
                <PlanPrice>${body?.price} USD</PlanPrice>
              </div>
            </PlanCard>

            {/* STORED CARDS DISPLAY CONTAINER without closing comment sequences inside */}
            <CardsSection>
              <SectionLabel>Select Card Payment Option</SectionLabel>

              {/* CARD 1: PRIMARY CARD without closing comment sequences inside */}
              <CardRowOption 
                active={selectedCardId === "primary"}
                onClick={() => paymentState !== "processing" && setSelectedCardId("primary")}
              >
                <CardDetailBlock>
                  <CardBrandAvatar>
                    <CardIcon brand={primaryCard.brand} />
                  </CardBrandAvatar>
                  <CardMetaBlock>
                    <CardHeadingText>{primaryCard.brand} Primary</CardHeadingText>
                    <CardNumberText>•••• •••• •••• {primaryCard.last4}</CardNumberText>
                  </CardMetaBlock>
                </CardDetailBlock>
                <RightRadioGroup>
                  <ExpiryLabel>{primaryCard.expiry}</ExpiryLabel>
                  <RadioCircle active={selectedCardId === "primary"}>
                    {selectedCardId === "primary" && <RadioInner />}
                  </RadioCircle>
                </RightRadioGroup>
              </CardRowOption>

              {/* CARD 2: BACKUP CARD without closing comment sequences inside */}
              <CardRowOption 
                active={selectedCardId === "backup"}
                onClick={() => paymentState !== "processing" && setSelectedCardId("backup")}
              >
                <CardDetailBlock>
                  <CardBrandAvatar>
                    <CardIcon brand={backupCard.brand} />
                  </CardBrandAvatar>
                  <CardMetaBlock>
                    <CardHeadingText>{backupCard.brand} Backup</CardHeadingText>
                    <CardNumberText>•••• •••• •••• {backupCard.last4}</CardNumberText>
                  </CardMetaBlock>
                </CardDetailBlock>
                <RightRadioGroup>
                  <ExpiryLabel>{backupCard.expiry}</ExpiryLabel>
                  <RadioCircle active={selectedCardId === "backup"}>
                    {selectedCardId === "backup" && <RadioInner />}
                  </RadioCircle>
                </RightRadioGroup>
              </CardRowOption>
            </CardsSection>

            {/* ACTION SECTION: Modify payment card parameters triggers the form flow without closing comment sequences inside */}
            {!showNewCardForm ? (
              <OutlineButton 
                type="button"
                onClick={handleOpenModifyForm}
                disabled={paymentState === "processing"}
              >
                Modify Payment / Enter Custom Card
              </OutlineButton>
            ) : (
              <NewCardFormContainer>
                <FormHeader>
                  <FormTitleText>Enter Custom Card Details</FormTitleText>
                  <FormCancelBtn 
                    type="button" 
                    onClick={handleCloseModifyForm} 
                  >
                    Cancel Custom Entry
                  </FormCancelBtn>
                </FormHeader>

                <FormFieldsWrapper>
                  {/* Card Holder Input without closing comment sequences inside */}
                  <InputWrapper>
                    <InputLabel>Cardholder Name</InputLabel>
                    <StyledInput 
                      type="text" 
                      value={newCardName}
                      onChange={(e) => setNewCardName(e.target.value)}
                      placeholder="e.g. Alex Carter"
                    />
                  </InputWrapper>

                  {/* Card Number Input with dynamic spacing without closing comment sequences inside */}
                  <InputWrapper>
                    <InputLabel>Card Number</InputLabel>
                    <StyledInput 
                      type="text" 
                      value={newCardNumber}
                      onChange={handleCardNumberChange}
                      placeholder="4000 1234 5678 9010"
                      monospace
                    />
                  </InputWrapper>

                  {/* Expiry and CVV inline column format without closing comment sequences inside */}
                  <FormInlineRow>
                    <InputWrapper>
                      <InputLabel>Expiry (MM/YY)</InputLabel>
                      <StyledInput 
                        type="text" 
                        value={newCardExpiry}
                        onChange={handleExpiryChange}
                        placeholder="09/29"
                        monospace
                      />
                    </InputWrapper>
                    <InputWrapper>
                      <InputLabel>CVV</InputLabel>
                      <StyledInput 
                        type="password" 
                        value={newCardCVV}
                        onChange={handleCVVChange}
                        placeholder="•••"
                        monospace
                      />
                    </InputWrapper>
                  </FormInlineRow>

                  {/* Save card checkbox indicator without closing comment sequences inside */}
                  <CheckboxRow>
                    <CheckboxInput 
                      type="checkbox" 
                      id="saveAsPrimary"
                      checked={saveAsPrimary}
                      onChange={(e) => setSaveAsPrimary(e.target.checked)}
                    />
                    <CheckboxLabelText htmlFor="saveAsPrimary">
                      Save card details as Primary payment option
                    </CheckboxLabelText>
                  </CheckboxRow>
                </FormFieldsWrapper>
              </NewCardFormContainer>
            )}

            {/* PROCESSING BAR INDICATION without closing comment sequences inside */}
            {paymentState === "processing" && (
              <ProgressCard>
                <ProgressHeader>
                  <span>Authorizing Gateway API</span>
                  <PulseStatus>Running...</PulseStatus>
                </ProgressHeader>
                <ProgressBarTrack>
                  <ProgressBarFill />
                </ProgressBarTrack>
              </ProgressCard>
            )}

            {/* PRIMARY SUBMIT ACTION TRIGGER without closing comment sequences inside */}
            <form onSubmit={handleProcessPayment}>
              <PrimaryButton 
                type="submit"
                disabled={paymentState === "processing"}
              >
                {paymentState === "processing" ? "Verifying Gateway Security..." : "Pay $5.00 USD securely"}
              </PrimaryButton>
            </form>

            <Disclaimer>
              By authorizing, you approve direct card processing settlement through Paystack/PayFast secure endpoints. Sensitive parameters are never cached.
            </Disclaimer>

          </>
        )}

      </ModalContainer>
    </ModalWrapper>
  );
}
